import React, { FC, useState, useEffect, useCallback, useRef } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { lang } from 'enablex/shared'
import api from '~/app/api'
import Select from 'react-select'
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Rectangle,
    PieChart,
    Pie,
    Cell
} from 'recharts'

import { AppState } from '~/app/rootReducer'
import { fetchAgentsStatsData } from '../../collaborators/reducer'
import { fetchBots } from '../../bots/reducer'
import PerformanceGraph from '../../collaborators/UserList/PerformanceGraph'
import PerformancePieChart from '../../collaborators/UserList/PerformancePieChart'
import TableContainer from '../../collaborators/UserList/TableContainer'
import PageContainer from '~/app/common/PageContainer'
import style from '../../collaborators/style.scss'

import StatWidget from './StatWidget'
import AvgSessionsIcon from '~/icons/AvgSessionsIcon'
import SessionsIcon from '~/icons/SessionsIcon'
import BotIcon from '~/icons/BotIcon'
import LiveAgentsIcon from '~/icons/LiveAgentsIcon'

type Props = ConnectedProps<typeof connector>

interface AgentBotStats {
  botId?: string
  totalRequests?: number
  resolvedRequests?: number
  unassignedRequests?: number
  expiredRequests?: number
  videoSessions?: number
  voiceSessions?: number
  resolveTime?: number
  [key: string]: any
}

const Dashboard: FC<Props> = props => {

    const [selectedAgentStatsData, setSelectedAgentStatsData] = useState<AgentBotStats[]>([])
    const [botNames, setBotNames] = useState({})
    const [agentBotAssignments, setAgentBotAssignments] = useState<AgentBotStats[]>([])
    const [fromDate, setFromDate] = useState('')
    const [toDate, setToDate] = useState('')
    const [selectedBot, setSelectedBot] = useState<string>('all') // Add state for selected bot

    // New state for supervisor dashboard data
    const [supervisorDashboardData, setSupervisorDashboardData] = useState({
        agentsAndBotsOverview: { totalAgents: 0, totalBots: 0, liveAgentsCount: 0, todaySessionCount: 0, lastweekSessionCount: 0 },
        sessionsOverview: { ongoingSessions: 0, totalAgents: 0, avgSessionsPerAgent: 0, last30DaysSessionCount: 0 }
    })

    // New state for agents session counts
    const [agentsSessionCounts, setAgentsSessionCounts] = useState<{ agentName: string; email: string; status: string; todaySessionCount: number }[]>([])

    // Add refs to track if data has been fetched
    const botsFetchedRef = useRef(false)
    const agentsStatsFetchedRef = useRef(false)

    // Get supervisor's bots for dropdown
    const supervisorBots = agentBotAssignments.map(bot => ({
        label: botNames[bot.botId || ''] || bot.botId || 'Unknown Bot',
        value: bot.botId || ''
    }))

    // Add "All Bots" option
    const botOptions = [
        { label: 'All Bots', value: 'all' },
        ...supervisorBots
    ]

    // Set default selected bot if none selected and bots are available
    useEffect(() => {
        if (selectedBot === 'all' && supervisorBots.length > 0 && supervisorBots[0].value) {
            setSelectedBot(supervisorBots[0].value)
        }
    }, [supervisorBots, selectedBot])

    // Calculate aggregated metrics for selected bot or all bots
    const getAggregatedMetrics = () => {
        if (selectedBot === 'all') {
            // Aggregate all bots
            return selectedAgentStatsData.reduce((acc, bot) => {
                Object.keys(acc).forEach(key => {
                    if (key !== 'resolveTime') {
                        acc[key] = (acc[key] || 0) + (bot[key] || 0)
                    }
                })
                // For resolve time, calculate average
                if (bot.resolveTime && bot.resolvedRequests && bot.resolvedRequests > 0) {
                    acc.resolveTime = (acc.resolveTime || 0) + (bot.resolveTime * bot.resolvedRequests)
                    acc.totalResolvedForAvg = (acc.totalResolvedForAvg || 0) + bot.resolvedRequests
                }
                return acc
            }, {
                totalRequests: 0,
                resolvedRequests: 0,
                unassignedRequests: 0,
                expiredRequests: 0,
                videoSessions: 0,
                audioOnlySessions: 0,
                voiceSessions: 0,
                resolveTime: 0,
                totalResolvedForAvg: 0
            })
        } else {
            // Get specific bot data
            const botData = selectedAgentStatsData.find(bot => bot.botId === selectedBot)
            return botData || {
                totalRequests: 0,
                resolvedRequests: 0,
                unassignedRequests: 0,
                expiredRequests: 0,
                videoSessions: 0,
                audioOnlySessions: 0,
                voiceSessions: 0,
                resolveTime: 0
            }
        }
    }

    const aggregatedMetrics = getAggregatedMetrics()
    const avgResolveTime = aggregatedMetrics.totalResolvedForAvg > 0
        ? (aggregatedMetrics.resolveTime || 0) / aggregatedMetrics.totalResolvedForAvg
        : 0

    function handleDateRangeChange() {
        // Trigger data refresh when date range changes
        void fetchSupervisorDashboardData()
    }

    function formatDate(dateString, dateOnly) {
        if (!dateString) {
            return ''
        }
        let date = new Date(dateString)
        let day = String(date.getUTCDate()).padStart(2, '0')
        let month = String(date.getUTCMonth() + 1).padStart(2, '0')
        let year = date.getUTCFullYear()
        let formattedDate
        if (dateOnly) {
            formattedDate = `${day}/${month}/${year}`
        } else {
            let hours = date.getUTCHours()
            let minutes = String(date.getUTCMinutes()).padStart(2, '0')
            let period = hours >= 12 ? 'PM' : 'AM'
            hours = hours % 12
            hours = hours ? hours : 12
            let hour = String(hours).padStart(2, '0')
            formattedDate = `${day}/${month}/${year} ${hour}:${minutes} ${period}`
        }
        return formattedDate
    }

    // Function to fetch supervisor dashboard data
    const fetchSupervisorDashboardData = useCallback(async () => {

        try {
           console.log('🔍 Fetching supervisor dashboard data')
            const [agentsAndBotsOverviewRes, sessionsOverviewRes] = await Promise.all([
                api.getSecured().get('/admin/workspace/collaborators/supervisor/dashboard/live-agents'),
                api.getSecured().get('/admin/workspace/collaborators/supervisor/dashboard/sessions-overview')
            ])

            setSupervisorDashboardData({
                agentsAndBotsOverview: agentsAndBotsOverviewRes.data.payload,
                sessionsOverview: sessionsOverviewRes.data.payload
            })
        } catch (error) {
            console.error('Error fetching supervisor dashboard data:', error)
        }
    }, [])

    useEffect(() => {
         console.log('🔍 Dashboard useEffect triggered:', {
           botsLength: props.bots.length,
           hasProfile: !!props.profile,
           isSupervisor: props.profile?.isSupervisor,
           profileEmail: props.profile?.email,
           botsFetched: botsFetchedRef.current,
           agentsStatsFetched: agentsStatsFetchedRef.current
         })

         // Only fetch bots if we don't have any bots loaded, we're not already loading, and we haven't fetched yet
         if (!props.bots.length && !props.loadingBots && !botsFetchedRef.current) {
            console.log('🔍 Fetching bots...')
            botsFetchedRef.current = true
            props.fetchBots()
        }

        // Only fetch agents stats data if we have a workspace, haven't loaded it yet, not already loading, and haven't fetched yet
        const workspaceId = localStorage.getItem('enx/workspace')
        if (workspaceId && (!props.agentsStatsData || props.agentsStatsData.length === 0) && !props.agentStatsLoading && !agentsStatsFetchedRef.current) {
            console.log('🔍 Fetching agents stats data...')
            agentsStatsFetchedRef.current = true
            props.fetchAgentsStatsData(workspaceId)
        }

        // Create bot map only when bots change
        const botMap = props.bots && Array.isArray(props.bots) ? props.bots.reduce((map, bot) => {
            map[bot.id] = bot.name
            return map
        }, {}) : {}

        setBotNames(botMap)

    }, []) // Empty dependency array - only run once on mount

    // Separate useEffect to handle bot names updates when bots change
    useEffect(() => {
        const botMap = props.bots && Array.isArray(props.bots) ? props.bots.reduce((map, bot) => {
            map[bot.id] = bot.name
            return map
        }, {}) : {}

        setBotNames(botMap)
    }, [props.bots])

    // Reset refs when component unmounts or when profile changes
    useEffect(() => {
        return () => {
            botsFetchedRef.current = false
            agentsStatsFetchedRef.current = false
        }
    }, [props.profile?.email])

    // Separate useEffect for profile-dependent operations
    useEffect(() => {
        // This useEffect only handles profile-dependent operations
        // and doesn't trigger API calls that could cause loops
        console.log('🔍 Profile useEffect triggered:', {
            isSupervisor: props.profile?.isSupervisor,
            profileEmail: props.profile?.email
        })
    }, [props.profile?.isSupervisor, props.profile?.email])

    // Consolidated useEffect for all supervisor dashboard data to prevent continuous calls
    useEffect(() => {
        if (!props.profile?.isSupervisor) {
            return
        }

        console.log('🚀 Fetching all supervisor dashboard data')

        const fetchAllSupervisorData = async () => {
            try {
                // Fetch all supervisor dashboard APIs in parallel
                const [
                    agentsAndBotsOverviewRes,
                    sessionsOverviewRes,
                    agentsSessionCountRes
                ] = await Promise.all([
                    api.getSecured().get('/admin/workspace/collaborators/supervisor/dashboard/live-agents'),
                    api.getSecured().get('/admin/workspace/collaborators/supervisor/dashboard/sessions-overview'),
                    api.getSecured().get('/admin/workspace/collaborators/supervisor/dashboard/agents-session-count')
                ])

                // Set supervisor dashboard data
                setSupervisorDashboardData({
                    agentsAndBotsOverview: agentsAndBotsOverviewRes.data.payload,
                    sessionsOverview: sessionsOverviewRes.data.payload
                })

                // Set agents session counts
                setAgentsSessionCounts(agentsSessionCountRes.data.payload || [])

            } catch (error) {
                console.error('Error fetching supervisor dashboard data:', error)
                setSupervisorDashboardData({
                    agentsAndBotsOverview: { totalAgents: 0, totalBots: 0, liveAgentsCount: 0, todaySessionCount: 0, lastweekSessionCount: 0 },
                    sessionsOverview: { ongoingSessions: 0, totalAgents: 0, avgSessionsPerAgent: 0, last30DaysSessionCount: 0 }
                })
                setAgentsSessionCounts([])
            }
        }

        void fetchAllSupervisorData()
    }, [props.profile?.isSupervisor])

    // Fetch agent performance data for logged-in supervisor
    useEffect(() => {
        if (!props.profile?.isSupervisor || !props.profile?.email) {
            return
        }

        console.log('🔍 Fetching agent performance data for supervisor:', props.profile.email)

        const fetchAgentPerformanceData = async () => {
            try {
                // Use the correct API for supervisors - agentsStatsData with workspace filtering
                const workspaceId = localStorage.getItem('enx/workspace') || 'default'
                const { data: agentsStatsResponse } = await api.getSecured().get(`/admin/workspace/collaborators/agentsStatsData/${workspaceId}`)
                const currentData = agentsStatsResponse.payload

                const agentsData = currentData?.map((data) => {
                    let filteredData = {}
                    const totalRequests = data.resolvedRequests
                    currentAgentHeadingKeys.forEach(key => {
                        if (data.hasOwnProperty(key)) {
                            if (key === 'createdAt' || key === 'firstRequestTime') {
                                filteredData[key] = formatDate(data[key], true)
                            } else if (key === 'lastRequestTime') {
                                filteredData[key] = formatDate(data[key], false)
                            } else if (key === 'resolveTime') {
                                if (totalRequests > 0) {
                                    const timeInSecs = data[key] / totalRequests
                                    filteredData[key] = timeInSecs
                                } else {
                                    filteredData[key] = ''
                                }
                            } else {
                                filteredData[key] = data[key]
                            }
                        }
                    })
                    return filteredData
                })
                console.log('🔍 Processed agent stats data:', agentsData)
                setSelectedAgentStatsData(agentsData)

                // Fetch agent's bot assignments
                try {
                    const { data: agentBotsData } = await api.getSecured().get(`/admin/workspace/bots/${props.profile?.email}/getBotsForAgent`)
                    const botIds = agentBotsData.payload || []

                    // Create bot assignment records for the TableContainer
                    const botAssignments = botIds.map(botId => ({
                        botId,
                        totalRequests: 0,
                        resolvedRequests: 0,
                        unassignedRequests: 0,
                        expiredRequests: 0,
                        videoSessions: 0,
                        voiceSessions: 0,
                        resolveTime: 0,
                        linkedAgent: true
                    }))

                    console.log('🔍 Bot assignments for supervisor:', botAssignments)
                    setAgentBotAssignments(botAssignments)
                } catch (botError) {
                    console.error('Error fetching agent bot assignments:', botError)
                    setAgentBotAssignments([])
                }
            } catch (error) {
                console.error('Error fetching agent stats:', error)
                setSelectedAgentStatsData([])
                setAgentBotAssignments([])
            }
        }

        void fetchAgentPerformanceData()
    }, [props.profile?.isSupervisor, props.profile?.email])

    // Performance matrics list
    const performanceMatricsList = [
        { label: 'Total Requests', value: 'totalRequests' },
        { label: 'Resolved Requests', value: 'resolvedRequests' },
        { label: 'Unassigned Requests', value: 'unassignedRequests' },
        { label: 'Expired Requests', value: 'expiredRequests' },
        { label: 'Resolve time', value: 'resolveTime' },
        { label: 'Video Sessions', value: 'videoSessions' },
        { label: 'Audio Only Sessions', value: 'audioOnlySessions' },
        { label: 'Voice Sessions', value: 'voiceSessions' }
    ]

    const currentAgentHeadingKeys = [
        'botId',
        'createdAt',
        'firstRequestTime',
        'lastRequestTime',
        'totalRequests',
        'resolvedRequests',
        'unassignedRequests',
        'expiredRequests',
        'resolveTime',
        'videoSessions',
        'audioOnlySessions',
        'voiceSessions',
        'linkedAgent'
    ]

    const performanceDefaultColor = '#8884d8'
    const performanceBackgroundColor = '#eee'

    // Dummy ticket data for recent tickets
    const dummyTickets = [
      {
        id: 1,
        subject: 'Feedback on portal design',
        customer: 'William Davis',
        priority: 'low',
        status: 'resolved',
        agent: 'Unknown Agent',
        time: 'about 6 hours ago',
        category: 'general',
      },
      {
        id: 2,
        subject: 'Refund request for cancelled subscription',
        customer: 'Jennifer Lee',
        priority: 'high',
        status: 'resolved',
        agent: 'Unknown Agent',
        time: 'about 6 hours ago',
        category: 'billing',
      },
      {
        id: 3,
        subject: 'Request for data export functionality',
        customer: 'Thomas Brown',
        priority: 'medium',
        status: 'active',
        agent: 'Unknown Agent',
        time: 'about 6 hours ago',
        category: 'feature request',
      },
      {
        id: 4,
        subject: 'System completely down, cannot access any features',
        customer: 'Maria Garcia',
        priority: 'urgent',
        status: 'escalated',
        agent: 'Unknown Agent',
        time: 'about 6 hours ago',
        category: 'technical',
      },
      {
        id: 5,
        subject: 'Question about new features',
        customer: 'Robert Jones',
        priority: 'low',
        status: 'resolved',
        agent: 'Unknown Agent',
        time: 'about 6 hours ago',
        category: 'general',
      },
      {
        id: 6,
        subject: "Billing inquiry about last month's charges",
        customer: 'Lisa Wang',
        priority: 'medium',
        status: 'pending',
        agent: 'Unknown Agent',
        time: 'about 6 hours ago',
        category: 'billing',
      },
      {
        id: 7,
        subject: 'Unable to access account after password reset',
        customer: 'John Smith',
        priority: 'high',
        status: 'active',
        agent: 'Unknown Agent',
        time: 'about 6 hours ago',
        category: 'technical',
      },
      {
        id: 8,
        subject: 'Unable to access account after password reset',
        customer: 'John Smith',
        priority: 'high',
        status: 'active',
        agent: 'Unassigned',
        time: 'about 6 hours ago',
        category: 'technical',
      },
      {
        id: 9,
        subject: 'Refund request for cancelled subscription',
        customer: 'Jennifer Lee',
        priority: 'high',
        status: 'resolved',
        agent: 'Unassigned',
        time: 'about 6 hours ago',
        category: 'billing',
      },
      {
        id: 10,
        subject: 'Request for data export functionality',
        customer: 'Thomas Brown',
        priority: 'medium',
        status: 'active',
        agent: 'Unassigned',
        time: 'about 6 hours ago',
        category: 'feature request',
      },
    ]

    // Helper for badge color
    const statusColor = status => {
      switch (status) {
        case 'resolved': return '#82ca9d'
        case 'active': return '#8884d8'
        case 'pending': return '#ffc658'
        case 'escalated': return '#ff4d4f'
        default: return '#d9d9d9'
      }
    }
    const priorityColor = priority => {
      switch (priority) {
        case 'low': return '#b7eb8f'
        case 'medium': return '#ffe58f'
        case 'high': return '#ffbb96'
        case 'urgent': return '#ff7875'
        default: return '#d9d9d9'
      }
    }

    // Tabs for ticket status
    const ticketTabs = [
      { key: 'all', label: 'All' },
      { key: 'active', label: 'Active' },
      { key: 'pending', label: 'Pending' },
      { key: 'resolved', label: 'Resolved' },
      { key: 'escalated', label: 'Escalated' },
    ]
    const [selectedTab, setSelectedTab] = useState('all')

    // Filtering tickets based on tab
    const filteredTickets = selectedTab === 'all'
      ? dummyTickets
      : dummyTickets.filter(ticket => ticket.status.toLowerCase() === selectedTab)

    // --- Agent Session Count Table ---
    // Removed the agentSessionTable variable since it's now integrated into the main layout

    const botChartData: AgentBotStats[] = (selectedAgentStatsData.length > 0 ? selectedAgentStatsData : agentBotAssignments) || []

    // Create proper bot data for supervisor's linked bots
    const supervisorBotData = agentBotAssignments.map(botAssignment => {
        // Find corresponding stats data for this bot
        const statsData = selectedAgentStatsData.find(stat => stat.botId === botAssignment.botId)
        return {
            botId: botAssignment.botId,
            totalRequests: statsData?.totalRequests || 0,
            resolvedRequests: statsData?.resolvedRequests || 0,
            unassignedRequests: statsData?.unassignedRequests || 0,
            expiredRequests: statsData?.expiredRequests || 0,
            videoSessions: statsData?.videoSessions || 0,
            audioOnlySessions: statsData?.audioOnlySessions || 0,
            voiceSessions: statsData?.voiceSessions || 0,
            resolveTime: statsData?.resolveTime || 0,
            linkedAgent: true
        }
    })

    console.log('🔍 Supervisor bot data:', supervisorBotData)

    return (
        <PageContainer title="Dashboard">
            <div>
                {/* Remove the original tickets section since it's now in the half-row format */}
                {/* END DUMMY RECENT TICKETS SECTION */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontFamily: 'Barlow Medium', fontSize: '24px', paddingLeft: '20px' }}>
                        My Dashboard
                        </span>
                    </div>
                </div>
                <div className={style.agentAnalyticsSectionContainer}>
                    {/* Show supervisor dashboard widgets for supervisors */}
                    {props.profile?.isSupervisor && (
                        <>
                            <div style={{ display: 'flex', gap: 24, marginBottom: 32 }}>
                                <div style={{ flex: '1 1 25%', minHeight: '120px' }}>
                                    <StatWidget
                                        icon={<LiveAgentsIcon />}
                                        value={`${supervisorDashboardData.agentsAndBotsOverview.liveAgentsCount} / ${supervisorDashboardData.agentsAndBotsOverview.totalAgents}`}
                                        label="Live Agents / Total Agents"
                                    />
                                </div>
                                <div style={{ flex: '1 1 25%', minHeight: '120px' }}>
                                    <StatWidget
                                        icon={<BotIcon />}
                                        value={supervisorDashboardData.agentsAndBotsOverview.totalBots}
                                        label="Total Bots"
                                    />
                                </div>
                                <div style={{ flex: '1 1 25%', minHeight: '120px' }}>
                                    <StatWidget
                                        icon={<SessionsIcon />} // You can replace with a calendar icon if available
                                        value={supervisorDashboardData.agentsAndBotsOverview.todaySessionCount}
                                        label="Today Sessions"
                                    />
                                </div>
                                <div style={{ flex: '1 1 25%', minHeight: '120px' }}>
                                    <StatWidget
                                        icon={<AvgSessionsIcon />}
                                        value={supervisorDashboardData.sessionsOverview.avgSessionsPerAgent}
                                        label="Avg Sessions/Agent"
                                    />
                                </div>
                            </div>

                            {/* --- Updated Sessions Summary to use StatWidget --- */}
                            {/* <div style={{ display: 'flex', gap: 24, marginBottom: 32 }}>
                                <div style={{ flex: '1 1 25%', minHeight: '120px' }}>
                                    <StatWidget
                                        icon={<SessionsIcon />}
                                        value={supervisorDashboardData.agentsAndBotsOverview.todaySessionCount}
                                        label="Today Sessions"
                                    />
                                </div>
                                <div style={{ flex: '1 1 25%', minHeight: '120px' }}>
                                    <StatWidget
                                        icon={<SessionsIcon />}
                                        value={supervisorDashboardData.agentsAndBotsOverview.lastweekSessionCount}
                                        label="Last Week Session Count"
                                    />
                                </div>
                                <div style={{ flex: '1 1 25%', minHeight: '120px' }}>
                                    <StatWidget
                                        icon={<SessionsIcon />}
                                        value={supervisorDashboardData.sessionsOverview.last30DaysSessionCount}
                                        label="Last 30 Days Session Count"
                                    />
                                </div>
                                <div style={{ flex: '1 1 25%', minHeight: '120px' }}>
                                </div>
                            </div> */}

                            {/* --- Restored My Performance Report and Bot Activity Comparison --- */}
                            <div style={{
                                display: 'flex',
                                gap: '30px',
                                marginBottom: '30px',
                                alignItems: 'stretch',
                            }}>
                                {/* My Performance Report (using TableContainer's built-in dropdown) */}
                                <div style={{
                                    flex: 1,
                                    background: 'white',
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.07)',
                                    padding: '28px 28px 20px 28px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minWidth: 0
                                }}>
                                    <style>
                                        {`
                                            .my-performance-report table {
                                                border-collapse: collapse;
                                            }
                                            .my-performance-report table th,
                                            .my-performance-report table td {
                                                border: 1px solid #e0e0e0;
                                                padding: 8px 12px;
                                            }
                                            .my-performance-report table th {
                                                background-color: #f8f8fa;
                                                font-weight: bold;
                                                border-bottom: 2px solid #d0d0d0;
                                            }
                                            .my-performance-report table tr:nth-child(even) {
                                                background-color: #fafafa;
                                            }
                                            .my-performance-report table tr:hover {
                                                background-color: #f5f5f5;
                                            }
                                        `}
                                    </style>
                                    {console.log('🔍 TableContainer data:', selectedAgentStatsData)}
                                    <div className="my-performance-report">
                                        <TableContainer
                                            data={supervisorBotData}
                                            botNames={botNames}
                                            tableValueHeading='Value'
                                        />
                                    </div>
                                </div>

                                {/* Bot Activity Comparison */}
                                <div style={{
                                    flex: 1,
                                    background: 'white',
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.07)',
                                    padding: '28px 28px 20px 28px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minWidth: 0
                                }}>
                                    <PerformanceGraph
                                        type='botPerformance'
                                        heading='Bot activity'
                                        dataKey='botName'
                                        agentsStatsData={supervisorBotData}
                                        performanceMatricsList={performanceMatricsList}
                                        botNames={botNames}
                                        defaultColor={performanceDefaultColor}
                                        backgroundColor={performanceBackgroundColor}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
            {/* Place My Agents at the very bottom, full width */}
            {props.profile?.isSupervisor && (
                <div style={{
                    marginTop: 32,
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.07)',
                    padding: '28px 28px 20px 28px',
                    minWidth: 0,
                    width: '100%',
                    display: 'block',
                }}>
                    <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, marginBottom: 18 }}>My Agents</h3>
                    <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                        {agentsSessionCounts && agentsSessionCounts.length > 0 ? (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid #eee' }}>
                                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 'bold', fontSize: '12px' }}>Agent Name</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 'bold', fontSize: '12px' }}>Status</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 'bold', fontSize: '12px' }}>Today Sessions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {agentsSessionCounts.map(agent => (
                                        <tr key={agent.email} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                            <td style={{ padding: '8px 12px', fontWeight: 500, fontSize: '12px' }}>{agent.agentName}</td>
                                            <td style={{ padding: '8px 12px', fontSize: '12px' }}>
                                                <span style={{
                                                    background: agent.status === 'Online' ? '#82ca9d' : '#ff7875',
                                                    color: 'white',
                                                    borderRadius: 8,
                                                    padding: '2px 8px',
                                                    fontSize: 10,
                                                    fontWeight: 500,
                                                    textTransform: 'capitalize'
                                                }}>
                                                    {agent.status}
                                                </span>
                                            </td>
                                            <td style={{ padding: '8px 12px', fontSize: '12px' }}>{agent.todaySessionCount}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#666', fontSize: '12px' }}>
                                No agents data available
                            </div>
                        )}
                    </div>
                </div>
            )}
        </PageContainer>
    )
}

const mapStateToProps = (state: AppState) => ({
    agentsStatsData: state.collaborators.agentsStatsData,
    bots: state.bots.bots,
    botsByAgent: state.bots.botsByAgent,
    users: state.collaborators.users,
    profile: state.user.profile,
    loadingBots: state.bots.loadingBots,
    agentStatsLoading: state.collaborators.agentStatsLoading
})

const connector = connect(mapStateToProps, { fetchAgentsStatsData, fetchBots })

export default connector(Dashboard)
